/**
 * Runtime configuration loaded from /runtime-config.json in production.
 * In dev, values come from Vite .env (import.meta.env.VITE_*).
 * Enables disaster recovery: swap config file without rebuilding the frontend.
 */

export type RuntimeConfig = {
  apiUrl: string;
  jainOnly: boolean;
  paypalClientId: string;
  paypalHostedButtonId: string;
  turnstileSiteKey: string;
  gaMeasurementId: string;
  productImagesBaseUrl: string;
  sendPublicIpForGeo: boolean;
  enableTenantTranslations: boolean;
  supabaseUrl: string;
  supabasePublishableKey: string;
};

type RuntimeConfigInput = Partial<Record<keyof RuntimeConfig, unknown>>;

let config: RuntimeConfig | null = null;

const DEFAULT_API_URL = "http://localhost:8000/api";

function envFlag(value: string | undefined): boolean {
  return value === "true";
}

function buildFromViteEnv(): RuntimeConfig {
  return {
    apiUrl: import.meta.env.VITE_JAIN_ELIBRARY_API_URL ?? DEFAULT_API_URL,
    jainOnly: envFlag(import.meta.env.VITE_JAIN_ONLY),
    paypalClientId: import.meta.env.VITE_PAYPAL_CLIENT_ID ?? "",
    paypalHostedButtonId: import.meta.env.VITE_PAYPAL_HOSTED_BUTTON_ID ?? "",
    turnstileSiteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "",
    gaMeasurementId: import.meta.env.VITE_GA_MEASUREMENT_ID ?? "",
    productImagesBaseUrl:
      import.meta.env.VITE_PRODUCT_IMAGES_BASE_URL ?? "/product-images",
    sendPublicIpForGeo: envFlag(import.meta.env.VITE_SEND_PUBLIC_IP_FOR_GEO),
    enableTenantTranslations: envFlag(
      import.meta.env.VITE_ENABLE_TENANT_TRANSLATIONS
    ),
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL ?? "",
    supabasePublishableKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? "",
  };
}

function mergeRuntimeConfig(
  base: RuntimeConfig,
  input: RuntimeConfigInput
): RuntimeConfig {
  const next = { ...base };
  if (typeof input.apiUrl === "string" && input.apiUrl.trim()) {
    next.apiUrl = input.apiUrl.trim();
  }
  if (typeof input.jainOnly === "boolean") {
    next.jainOnly = input.jainOnly;
  }
  if (typeof input.paypalClientId === "string") {
    next.paypalClientId = input.paypalClientId;
  }
  if (typeof input.paypalHostedButtonId === "string") {
    next.paypalHostedButtonId = input.paypalHostedButtonId;
  }
  if (typeof input.turnstileSiteKey === "string") {
    next.turnstileSiteKey = input.turnstileSiteKey;
  }
  if (typeof input.gaMeasurementId === "string") {
    next.gaMeasurementId = input.gaMeasurementId;
  }
  if (
    typeof input.productImagesBaseUrl === "string" &&
    input.productImagesBaseUrl
  ) {
    next.productImagesBaseUrl = input.productImagesBaseUrl;
  }
  if (typeof input.sendPublicIpForGeo === "boolean") {
    next.sendPublicIpForGeo = input.sendPublicIpForGeo;
  }
  if (typeof input.enableTenantTranslations === "boolean") {
    next.enableTenantTranslations = input.enableTenantTranslations;
  }
  if (typeof input.supabaseUrl === "string") {
    next.supabaseUrl = input.supabaseUrl;
  }
  if (typeof input.supabasePublishableKey === "string") {
    next.supabasePublishableKey = input.supabasePublishableKey;
  }
  return next;
}

export function isRuntimeConfigLoaded(): boolean {
  return config !== null;
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!config) {
    throw new Error(
      "Runtime config not loaded. Call loadRuntimeConfig() first."
    );
  }
  return config;
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (config) {
    return config;
  }

  const fromEnv = buildFromViteEnv();

  if (import.meta.env.DEV) {
    config = fromEnv;
    return config;
  }

  try {
    const configUrl = `${import.meta.env.BASE_URL}runtime-config.json`.replace(
      /([^:]\/)\/+/g,
      "$1"
    );
    const response = await fetch(configUrl, { cache: "no-store" });
    if (response.ok) {
      const json = (await response.json()) as RuntimeConfigInput;
      config = mergeRuntimeConfig(fromEnv, json);
      return config;
    }
    console.warn(
      `[runtime-config] ${configUrl} returned ${response.status}; using build-time defaults.`
    );
  } catch (error) {
    console.warn("[runtime-config] Failed to load runtime-config.json:", error);
  }

  config = fromEnv;
  return config;
}
